import type { LoginResponse, SessionUser } from "@eduleo/shared";
import { create } from "zustand";
import { LOGIN_PATH, signInPathFor } from "../lib/sign-in-paths";

export type AuthStatus = "loading" | "signed_out" | "signed_in";

interface AuthState {
  status: AuthStatus;
  user: SessionUser | null;
  /**
   * Kept in memory only, never in localStorage, so injected scripts can't lift it from
   * storage. A page reload gets a fresh one from the httpOnly refresh cookie.
   */
  accessToken: string | null;
  /**
   * The sign-in page of the last person signed in here (school or Eduleo team), so someone
   * whose session ends lands back where they sign in. Memory only, and not personal.
   */
  signInPath: string;
  setSession: (session: LoginResponse) => void;
  clearSession: () => void;
  /** The API said this person must choose their own password before doing anything else. */
  requirePasswordChange: () => void;
}

export const useAuthStore = create<AuthState>()((set) => ({
  status: "loading",
  user: null,
  accessToken: null,
  signInPath: LOGIN_PATH,
  setSession: (session) =>
    set({
      status: "signed_in",
      user: session.user,
      accessToken: session.accessToken,
      signInPath: signInPathFor(session.user),
    }),
  clearSession: () => set({ status: "signed_out", user: null, accessToken: null }),
  requirePasswordChange: () =>
    set((state) =>
      state.user && !state.user.mustChangePassword
        ? { user: { ...state.user, mustChangePassword: true } }
        : state,
    ),
}));
